import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetApplicationsQuery, usePostUpsertApplicationMutation } from "@/store/service";
import ApplicationRequest from "@/model/interface/ApplicationRequest";
import { ApplicationType } from "@/enum/ApplicationType";
import { useTitle } from "@/components/provider/TitleProvider";
import ExistingApplications from "../../ExistingApplications";
import { ApiApplication } from "@/model/interface/api/ApiApplication";
import Skeleton from "react-loading-skeleton";
import HomeEstimatedValue from "@/components/shared/HomeEstimatedValue";
import MortgageEstOutstanding from "@/components/shared/MortgageEstOutstanding";
import MortgageMonthlyRepay from "@/components/shared/MortgageMonthlyRepay";
import MortgageHelpPercentage from "@/components/shared/MortgageHelpPercentage";
import ProductShareInformation from "@/components/shared/ProductShareInformation";
import { getUrlStepFromApplicationStep } from "@/util/url-utils";
import { isDebugForms } from "@/util/debug";

const MonthlyBoostCalculator = () => {
  const navigate = useNavigate();
  const { title, setTitle } = useTitle();
  const { data: userApplications, isSuccess, isFetching } = useGetApplicationsQuery();
  const [postCreateApplicationMutation, postCreateApplicationMutationResult] = usePostUpsertApplicationMutation();

  const [newApplicationSelected, setNewApplicationSelected] = useState(false);
  const [isExistingApplicationNonAuth, setExistingApplicationNonAuth] = useState(false);
  const [existingHomeownerAssistantApplications, setExistingHomeownerAssistantApplications] = useState<
    ApiApplication[]
  >([]);

  const form = useForm<ApplicationRequest>({
    mode: "onTouched",
    defaultValues: {},
  });

  const {
    register,
    trigger,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  useEffect(() => {
    setTitle("");
  }, []);

  useEffect(() => {
    if (!isExistingApplicationNonAuth && isSuccess && userApplications.length) {
      const homeownerAssistantApplications = userApplications.filter(
        (application) => application.type === ApplicationType.HomeownerAssistant,
      );
      if (homeownerAssistantApplications) {
        setExistingHomeownerAssistantApplications(homeownerAssistantApplications);
      }
    }
  }, [isExistingApplicationNonAuth, isSuccess, userApplications]);

  useEffect(() => {
    if (postCreateApplicationMutationResult.isSuccess) {
      if (!Object.keys(postCreateApplicationMutationResult.data).length) {
        setExistingApplicationNonAuth(true);
      } else {
        navigate(
          `/application/${postCreateApplicationMutationResult.data.id}/${getUrlStepFromApplicationStep(postCreateApplicationMutationResult.data.step)}`,
        );
        return;
      }
    }
  }, [postCreateApplicationMutationResult.isSuccess]);

  const onSubmit = async (data: ApplicationRequest) => {
    if (isValid) {
      const data = getValues();
      data.type = ApplicationType.HomeownerAssistant;
      postCreateApplicationMutation(data);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDebugForms()) {
      navigate("/monthly-boost/register");
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  if (isFetching) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-6">
        <Skeleton count={5} />
      </div>
    );
  }

  if (existingHomeownerAssistantApplications.length && !newApplicationSelected) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-6 space-y-6">
        <ExistingApplications applications={existingHomeownerAssistantApplications} type="Homeowner assistant" />
        <button
          onClick={() => {
            setNewApplicationSelected(true);
            trigger();
          }}
          className="inline-flex items-center rounded-full bg-[#9BFBE0] px-6 py-2.5 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
        >
          New application
        </button>
      </div>
    );
  }

  if (isExistingApplicationNonAuth && !newApplicationSelected) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-6">
        <p>Email sent.</p>
      </div>
    );
  }

  if (!isExistingApplicationNonAuth || newApplicationSelected) {
    return (
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <div className="w-full max-w-2xl mx-auto px-4 py-6 space-y-6">
            <h3 className="text-4xl font-semibold text-gray-900">Monthly Boost Calculator</h3>
            <HomeEstimatedValue fieldPrefix="context" />
            <MortgageEstOutstanding fieldPrefix="context" />
            <MortgageMonthlyRepay fieldPrefix="context" />
            <MortgageHelpPercentage fieldPrefix="context" />
            <ProductShareInformation applicationType={ApplicationType.HomeownerAssistant} fieldPrefix={"context"} />
            <div className="flex justify-center pt-2">
              <button
                type="submit"
                className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
              >
                Get Started
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    );
  }

  return <></>;
};

MonthlyBoostCalculator.propTypes = {};

export default MonthlyBoostCalculator;
